import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, auth, getToken, setToken } from "./api.ts";

export interface Me {
  user: { id: string; name: string; phone: string | null };
  host: { status: "pending" | "verified" | "suspended"; displayName: string } | null;
}
interface Session {
  /** undefined while checking, null when signed out */
  me: Me | null | undefined;
  refresh: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}
const Ctx = createContext<Session | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null | undefined>(getToken() ? undefined : null);
  const refresh = useCallback(async () => {
    if (!getToken()) { setMe(null); return; }
    try { setMe(await api<Me>("/account/me")); } catch { setToken(null); setMe(null); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  const value = useMemo<Session>(() => ({
    me, refresh,
    signIn: async (email, password) => { await auth("/sign-in/email", { email, password }); await refresh(); },
    signUp: async (name, email, password) => { await auth("/sign-up/email", { name, email, password }); await refresh(); },
    signOut: async () => { await auth("/sign-out", {}).catch(() => undefined); setToken(null); setMe(null); },
  }), [me, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): Session {
  const v = useContext(Ctx);
  if (!v) throw new Error("AuthProvider missing");
  return v;
}
